/**
 * draftHistoryPresenters.js — view models for DraftHistoryModal.
 *
 * Pure functions over a stored `auctionHistory` season. Kept out of the JSX so
 * the ordering, joining and grouping rules can be checked headlessly against a
 * real league (src/tools/draftHistoryVerify.scratch.js) rather than by eye.
 *
 * Pick shape (sleeperImport.fetchAuctionDraftPicks):
 *   { name, pos, amount, keeper, pickedBy, rosterId, pickNo, draftSlot }
 * Member shape (sleeperImport.fetchLeagueMembers):
 *   { displayName, teamName, userId }
 *
 * pickNo/pickedBy are riders added mid-July 2026. Histories saved before that
 * have neither, and re-importing is the only way to backfill — so both views
 * degrade rather than fail: Board falls back to price order, Rosters needs only
 * pickedBy (or rosterId) and is unaffected by a missing pickNo.
 */

/** userId → member, for joining pick.pickedBy to a display name. */
export function buildManagerIndex(members) {
  const index = new Map();
  for (const m of members || []) {
    if (m?.userId) index.set(m.userId, m);
  }
  return index;
}

/**
 * Who bought this player, as text. displayName is the name the league knows
 * each other by; "Team {rosterId}" is the honest fallback when the members
 * fetch failed or the league was imported before members were captured —
 * a seat number is still more use than a blank cell.
 */
export function ownerLabel(pick, index) {
  const member = pick?.pickedBy ? index.get(pick.pickedBy) : null;
  if (member?.displayName) return member.displayName;
  if (pick?.rosterId != null) return `Team ${pick.rosterId}`;
  return "—";
}

/**
 * Stable grouping key for a pick's owner. pickedBy is the real identity and
 * survives seat reshuffles; rosterId is the season-local seat, used only when
 * pickedBy is absent. Picks with neither can't be seated at all.
 */
export function ownerKey(pick) {
  if (pick?.pickedBy) return `u:${pick.pickedBy}`;
  if (pick?.rosterId != null) return `r:${pick.rosterId}`;
  return null;
}

/**
 * Board view — the draft as it happened, in sale order.
 *
 * `trueOrder` is false unless EVERY pick carries a pickNo: a partially
 * numbered season would sort into a plausible-looking order that isn't the
 * real one, which is worse than admitting the order is unknown. In that case
 * rows come back price-desc (the pre-v2 behaviour) and the caller shows the
 * re-import note.
 */
export function buildBoardView(season, members) {
  const index = buildManagerIndex(members);
  const picks = [...(season?.picks || [])];
  const trueOrder = picks.length > 0 && picks.every(p => Number.isFinite(p.pickNo));

  const ordered = trueOrder
    ? picks.sort((a, b) => a.pickNo - b.pickNo)
    : picks.sort((a, b) => b.amount - a.amount || a.name.localeCompare(b.name));

  return {
    trueOrder,
    rows: ordered.map(p => ({
      pick: p,
      // Only labelled with a number when the number is real.
      no: trueOrder ? p.pickNo : null,
      owner: ownerLabel(p, index),
    })),
    total: picks.reduce((sum, p) => sum + p.amount, 0),
  };
}

/**
 * Rosters view — one panel per team, the way the in-app rosters modal reads.
 *
 * Panels come back in seat order (rosterId asc) so the same manager sits in the
 * same place season to season; leagues with no rosterId fall back to name
 * order. Picks inside a panel are price-desc — a roster's story is its top buys,
 * not the order they happened to come up.
 *
 * `orphanCount`/`orphanTotal` cover picks that can't be seated (no pickedBy and
 * no rosterId — every pre-rider history). They're reported rather than dropped
 * so panel totals can always be reconciled against the season total.
 */
export function buildRosterView(season, members) {
  const index = buildManagerIndex(members);
  const picks = season?.picks || [];

  const groups = new Map();
  let orphanCount = 0;
  let orphanTotal = 0;

  for (const p of picks) {
    const key = ownerKey(p);
    if (!key) {
      orphanCount += 1;
      orphanTotal += p.amount;
      continue;
    }
    let g = groups.get(key);
    if (!g) {
      g = {
        key,
        manager: ownerLabel(p, index),
        // Seat number drives panel order; null sorts last.
        rosterId: p.rosterId ?? null,
        picks: [],
        total: 0,
        keeperCount: 0,
      };
      groups.set(key, g);
    }
    // A pick can carry a rosterId even when the first one for this manager
    // didn't — take the first non-null we see rather than leaving the panel
    // unsorted.
    if (g.rosterId == null && p.rosterId != null) g.rosterId = p.rosterId;
    g.picks.push(p);
    g.total += p.amount;
    if (p.keeper) g.keeperCount += 1;
  }

  const panels = [...groups.values()];
  for (const g of panels) {
    g.picks.sort((a, b) => b.amount - a.amount || a.name.localeCompare(b.name));
  }
  panels.sort((a, b) => {
    if (a.rosterId != null && b.rosterId != null) return a.rosterId - b.rosterId;
    if (a.rosterId != null) return -1;
    if (b.rosterId != null) return 1;
    return a.manager.localeCompare(b.manager);
  });

  return {
    panels,
    orphanCount,
    orphanTotal,
    total: picks.reduce((sum, p) => sum + p.amount, 0),
  };
}
